import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Role hierarchy for permissions
const ROLE_HIERARCHY = {
  owner: 4,
  partner: 3,
  associate: 2,
  paralegal: 1,
  admin: 1,
  client: 0
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      // Check for impersonation token first
      const impersonationToken = localStorage.getItem('impersonation_token');
      const impersonationSession = localStorage.getItem('impersonation_session');
      
      if (impersonationToken && impersonationSession) {
        // Set the impersonation token as active
        localStorage.setItem('token', impersonationToken);
        setIsImpersonating(true);
        
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (err) {
          // Impersonation token expired or invalid
          localStorage.removeItem('impersonation_token');
          localStorage.removeItem('impersonation_session');
          setIsImpersonating(false);
          
          // Try regular auth
          const adminToken = localStorage.getItem('admin_token');
          if (adminToken) {
            localStorage.setItem('token', adminToken);
            window.location.href = '/admin';
            return;
          }
        }
      } else {
        // Regular auth check
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        if (token && storedUser) {
          try {
            const response = await authAPI.getMe();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          } catch (err) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        return { 
          success: false, 
          mfaRequired: true, 
          tempToken: response.data.temp_token 
        };
      }
      
      const { token, user: userData, needs_subscription_selection } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true, needsSubscriptionSelection: needs_subscription_selection };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password, firmName) => {
    setError(null);
    try {
      const response = await authAPI.register({ 
        name, 
        email, 
        password, 
        firm_name: firmName 
      });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    setError(null);
    try {
      const response = await authAPI.exchangeSession(sessionId);
      const userData = response.data;
      
      localStorage.setItem('token', userData.session_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.detail || 'OAuth callback failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Update user data (e.g., after firm switch)
  const updateUser = (userData) => {
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  };

  // All features are now available - no tier restrictions
  const hasFeature = (feature) => {
    return !!user;
  };

  // Check if user has minimum role level
  const hasRole = (requiredRole) => {
    if (!user) return false;
    const userLevel = ROLE_HIERARCHY[user.role] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    return userLevel >= requiredLevel;
  };

  // Check if user is owner/superadmin
  const isOwner = () => {
    return user?.role === 'owner';
  };

  // Check if user can manage team
  const canManageTeam = () => {
    return user?.role === 'owner' || user?.role === 'partner';
  };

  // Check if user can assign cases
  const canAssignCases = () => {
    return ['owner', 'partner', 'associate'].includes(user?.role);
  };

  const value = {
    user,
    loading,
    error,
    setError,
    isAuthenticated: !!user,
    isImpersonating,
    login,
    register,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    updateUser,
    hasFeature,
    hasRole,
    isOwner,
    canManageTeam,
    canAssignCases,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
